import React from 'react'
import { AnimatePresence } from 'framer-motion'
import styled from 'styled-components'

import { TowingRecordPaymentType, TowingRecordRouteType, TypeIdentifier } from '../../common/types'
import autoliitto from '../../images/autoliitto.png'
import lahitapiola from '../../images/lahitapiola.png'
import sos from '../../images/sos.png'
import groupTransfer from '../../images/group_transfer.svg'
import { DisplayDate } from '../DisplayDate'
import { Icon } from '../icons/Icon'
import { JobStatus } from '../JobStatus'
import { JobTypeDescription, getDeliverable } from '../JobTypeDescription'
import {
  ExpandedList,
  Heading3,
  ImageWrapper,
  List,
  ListColumn,
  ListRowWrapper,
  ListStatus,
  SimpleRow,
  Text,
} from '../layout'
import { Styling } from '../Styling'
import { NavigateTo } from '../NavigateTo'
import { getPrepaymentLabel } from '../TowingCommon/getPrepaymentLabel'
import { VehicleMarkAndModel } from '../VehicleMarkAndModel'
import { TowingRecordRowInterface } from './types'
import { getRepairShop } from './getRepairShop'
import { useSocketLock } from '../../util/useSocketLock'
import { parseAddress } from '../Parsers'
import { RepairShop } from '../../containers/TowingRecordPage/components/routes/subforms/FinishRoute/options'

interface Props {
  repairShops: RepairShop[]
  towingRecord: TowingRecordRowInterface
  translateMessage: (messageId: string) => string
  slug: string
  expandedRow: boolean
  handleItemClick: (id: number) => void
  station: {
    name: string
  } | null
  operatorName: string
}

const StyledIcon = styled(Icon as any)`
  svg {
    width: 1rem;
    height: 1rem;
    margin-left: -2px;
  }
`

export const TowingRecordRow = React.memo<Props>(function TowingRecordRow({
  expandedRow,
  handleItemClick,
  towingRecord,
  slug,
  station,
  operatorName,
  repairShops,
}) {
  const fromIndex = towingRecord.routes.findIndex(route => route.type === TowingRecordRouteType.arrival)
  const toIndex = towingRecord.routes.findIndex(route => route.type === TowingRecordRouteType.finish)

  const repairShop = getRepairShop(repairShops, towingRecord)

  const recordNumber = towingRecord.recordNumber || '-'
  const registrationNumber = towingRecord.vehicleDetails.registrationNumber || '-'
  const notUnfinished = towingRecord.status !== 'unfinished'
  const regNumber = notUnfinished ? registrationNumber + ' / ' + recordNumber : registrationNumber

  const locked = useSocketLock({
    entityType: 'towing_record',
    entityId: towingRecord.id,
    initialLockedFor: towingRecord.locked,
  })

  const billingStatus = towingRecord.billing?.[0]?.status
  const normalizedStatus = billingStatus === 'sent' ? 'link_sent' : billingStatus
  const paymentLinkLabel =
    towingRecord.paymentType === TowingRecordPaymentType.online_payment &&
    getPrepaymentLabel(normalizedStatus, {
      fallback: 'maksulinkki lähetetty',
      prepayment: towingRecord.prepayment,
    })

  return (
    <ListRowWrapper
      onClick={() => handleItemClick(towingRecord.id)}
      isCanceled={
        towingRecord.sosServiceOrder && towingRecord.sosServiceOrder.orderStatus === 'cancelled_by_sos' ? true : false
      }
    >
      <List>
        <ListColumn justify="center">
          <Heading3>{regNumber}</Heading3>
          <VehicleMarkAndModel markAndModel={towingRecord.vehicleDetails.makeAndModel} />
          <Text>
            <DisplayDate date={towingRecord.jobDetails.towingDate} format="full" />
          </Text>
        </ListColumn>
        <ListColumn justify="center">
          {towingRecord.typeIdentifier === TypeIdentifier.autoliitto && (
            <ImageWrapper justifyContent="center" alignItems="center">
              <img src={autoliitto} alt="Autoliitto" width="55rem" />
            </ImageWrapper>
          )}
          {towingRecord.typeIdentifier === TypeIdentifier.sos && (
            <ImageWrapper justifyContent="center" alignItems="center">
              <img src={sos} alt="SOS" width={`40rem`} />
            </ImageWrapper>
          )}
          {towingRecord.typeIdentifier === TypeIdentifier.lahitapiola && (
            <ImageWrapper justifyContent="center" alignItems="center">
              <img src={lahitapiola} alt="Lähitapiola" width={`40rem`} />
            </ImageWrapper>
          )}
          {towingRecord.typeIdentifier === TypeIdentifier.group_transfer && (
            <ImageWrapper justifyContent="center" alignItems="center">
              <img src={groupTransfer} alt="Other" width={`55rem`} />
            </ImageWrapper>
          )}
        </ListColumn>
        <ListColumn>
          <ListStatus status={towingRecord.status}>
            <JobStatus status={towingRecord.status} operatorName={operatorName} jobStatus={towingRecord.jobStatus} />
          </ListStatus>
          <Text color="grey400">
            <JobTypeDescription
              serviceType={towingRecord.jobDetails.serviceType}
              type={towingRecord.typeIdentifier}
              label={towingRecord.label}
              station={station}
              deliverable={getDeliverable(towingRecord.sosServiceOrder)}
            />
          </Text>
          {paymentLinkLabel && (
            <Styling bold>
              <Text size="small">{paymentLinkLabel}</Text>
            </Styling>
          )}
        </ListColumn>
        <ListColumn>
          <NavigateTo direction={locked ? 'locked' : 'forward'} slug={slug} />
        </ListColumn>
      </List>
      <AnimatePresence>
        {expandedRow && (
          <ExpandedList key={towingRecord.id}>
            <ListColumn>
              <SimpleRow justify={'flex-start'} marginMobileBottom="0">
                <StyledIcon icon="map-marker" color="black" marginRight="0.3rem" size="x-small" />
                {fromIndex > -1 ? (
                  <Text>{`${towingRecord.routes[fromIndex].address} ${
                    towingRecord.routes[fromIndex].city
                  } ${towingRecord.routes[fromIndex].zipcode || ''}`}</Text>
                ) : (
                  '-'
                )}
              </SimpleRow>
              {fromIndex > -1 && towingRecord.routes[fromIndex].description !== '' && (
                <SimpleRow marginMobileBottom="0" margin="0.25rem 0 0.25rem 1.25rem">
                  <Text>{`(${towingRecord.routes[fromIndex].description})`}</Text>
                </SimpleRow>
              )}
              <SimpleRow justify={'flex-start'} marginMobileBottom="0">
                <StyledIcon icon="map-marker" color="black" marginRight="0.3rem" size="x-small" />
                {toIndex > -1 && towingRecord.routes[toIndex].address ? (
                  <Text>{`${towingRecord.routes[toIndex].address} ${towingRecord.routes[toIndex].city} ${towingRecord
                    .routes[toIndex].zipcode || ''}`}</Text>
                ) : repairShop &&
                  towingRecord.typeIdentifier === TypeIdentifier.autoliitto &&
                  towingRecord.jobDetails.serviceType !== 'towing' ? (
                  <Text fontWeight="bold">{`Jos työ muuttuu hinaukseksi:`}</Text>
                ) : (
                  '-'
                )}
              </SimpleRow>
              {toIndex > -1 && towingRecord.routes[toIndex].description !== '' ? (
                <SimpleRow marginMobileBottom="0" margin="0.25rem 0 0.25rem 1.25rem">
                  <Text>{`(${towingRecord.routes[toIndex].description})`}</Text>
                </SimpleRow>
              ) : (
                towingRecord.typeIdentifier === TypeIdentifier.autoliitto &&
                towingRecord.jobDetails.serviceType !== 'towing' &&
                repairShop && (
                  <>
                    <SimpleRow marginMobileBottom="0" margin="0.25rem 0 0.25rem 1.25rem">
                      <Text>{parseAddress(repairShop.location.address, repairShop.location.city)}</Text>
                    </SimpleRow>
                    <SimpleRow marginMobileBottom="0" margin="0 0 0.25rem 1.25rem">
                      <Text>{repairShop.name}</Text>
                    </SimpleRow>
                  </>
                )
              )}
            </ListColumn>
            <ListColumn />
            <ListColumn>
              <Text>{towingRecord.jobDetails.notes}</Text>
            </ListColumn>
            <ListColumn />
          </ExpandedList>
        )}
      </AnimatePresence>
    </ListRowWrapper>
  )
})
